import React, { Component } from 'react';
import { connect } from 'react-redux';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import axios from '../../../axios-url';

import { HASACCESS } from '../../../shared/utility';
import { ZATCA_COMPANY_SUBMENU_ID } from '../../../shared/Config';
import Messages from '../../../shared/UI/Messages/Messages';
import FooterButton from '../../../shared/UI/FooterButton/FooterButton';
import * as actions from '../../../store/actions/index';
import { validateZatcaCompany, apiErrorMessage } from './zatcaShared';

const noteStyle = {
    padding: '0.75em 1em',
    borderRadius: '4px',
    background: '#fff4e5',
    border: '1px solid #f5c26b',
    color: '#6b4500',
    marginBottom: '1em'
};

/**
 * The seller details ZATCA puts on every invoice and into every branch unit's
 * certificate. They are stored in existing COMPANY columns that no other screen
 * edits.
 *
 * Once any branch unit has started onboarding the details are locked: they are
 * already inside a certificate, and changing them means onboarding again.
 */
class ZatcaCompany extends Component {
    state = {
        access: undefined,
        loading: false,
        touch: false,
        isLocked: false,
        companyName: '',
        nameAr: '',
        vatNo: '',
        street: '',
        buildingNo: '',
        additionalNo: '',
        district: '',
        city: '',
        postalCode: '',
        countryCode: 'SA'
    }

    componentDidMount() {
        this.setState({ access: HASACCESS(ZATCA_COMPANY_SUBMENU_ID) });
        this.load();
    }

    componentDidUpdate(prevProps) {
        // A successful save leaves nothing unsaved on the form.
        if (this.props.success !== null && this.props.success !== prevProps.success) {
            this.setState({ touch: false });
        }
    }

    load = () => {
        this.setState({ loading: true });
        axios.get('/master/api/zatca/GetZatcaCompany?companyId=' + this.props.loggedUserDetail.CompanyId)
            .then(response => {
                const company = response.data || {};
                this.setState({
                    loading: false,
                    touch: false,
                    isLocked: company.isLocked === true,
                    companyName: company.companyName || '',
                    nameAr: company.nameAr || '',
                    vatNo: company.vatNo || '',
                    street: company.street || '',
                    buildingNo: company.buildingNo || '',
                    additionalNo: company.additionalNo || '',
                    district: company.district || '',
                    city: company.city || '',
                    postalCode: company.postalCode || '',
                    countryCode: company.countryCode || 'SA'
                });
            })
            .catch(err => {
                this.setState({ loading: false });
                this.growl.show({ severity: 'error', summary: null, detail: apiErrorMessage(err, 'Could not load the company ZATCA details') });
            });
    }

    change = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value, touch: true });
    }

    save = () => {
        const errors = validateZatcaCompany(this.state);
        if (errors.length > 0) {
            errors.forEach(error => this.growl.show({ severity: 'error', summary: 'Validation Error', detail: error }));
            return;
        }

        const data = {
            CompanyId: this.props.loggedUserDetail.CompanyId,
            NameAr: this.state.nameAr.trim(),
            VatNo: this.state.vatNo.trim(),
            Street: this.state.street.trim(),
            BuildingNo: this.state.buildingNo.trim(),
            AdditionalNo: this.state.additionalNo.trim(),
            District: this.state.district.trim(),
            City: this.state.city.trim(),
            PostalCode: this.state.postalCode.trim(),
            CountryCode: 'SA',
            UpdatedBy: this.props.loggedUserDetail.userName ?? 'Admin',
            UpdatedDate: new Date()
        };

        this.props.processDynamicData('ZatcaCompany', 'edit', data, '');
    }

    field = (label, name, options = {}) => (
        <div className="p-col-12 p-md-6">
            <div className="p-col-12">
                <label htmlFor={'zatca-company-' + name}>
                    {label} {options.required && <span className="requiredColor">*</span>}
                </label>
            </div>
            <div className="p-col-12">
                <InputText
                    id={'zatca-company-' + name}
                    className="p-col-12"
                    name={name}
                    value={this.state[name]}
                    onChange={this.change}
                    disabled={options.disabled}
                    dir={options.dir}
                    maxLength={options.maxLength}
                    keyfilter={options.keyfilter}
                    placeholder={options.placeholder}
                />
                {options.hint && <small>{options.hint}</small>}
            </div>
        </div>
    )

    render() {
        const access = this.state.access;
        if (access !== undefined && access.viewAccess === false) {
            this.props.history.push('/dashboard');
        }

        const editAccess = access !== undefined && access.editAccess === true;
        const disabled = this.state.isLocked || !editAccess;

        return (
            <div className="p-grid">
                {(this.props.loading || this.state.loading) && <div className="page-loader"></div>}
                {this.props.error !== null && <Messages type="error" message={this.props.error} />}
                {this.props.success !== null && <Messages type="success" message={this.props.success} />}
                <Toast ref={(el) => this.growl = el} />
                <div className="p-col-12">
                    <div className="card card-w-title" style={{ position: 'relative' }}>
                        <h1>ZATCA Company Details</h1>
                        <p>
                            These details appear on every e-invoice and in each branch unit's ZATCA certificate.
                            Enter them exactly as registered with ZATCA, not as written on the letterhead.
                        </p>

                        {this.state.isLocked &&
                            <div style={noteStyle}>
                                Locked: at least one branch unit has started ZATCA onboarding, so these details are
                                already in its certificate. Changing them means onboarding those units again.
                            </div>
                        }

                        <div className="p-grid mt1">
                            {this.field('Company name', 'companyName', { disabled: true, hint: 'From Company Setting' })}
                            {this.field('Legal name (Arabic)', 'nameAr', { required: true, disabled, dir: 'rtl', hint: 'As registered with ZATCA' })}
                            {this.field('VAT number', 'vatNo', { required: true, disabled, maxLength: 15, keyfilter: 'pint', placeholder: '3XXXXXXXXXXXXX3', hint: '15 digits, starting and ending with 3' })}
                            {this.field('Street', 'street', { required: true, disabled })}
                            {this.field('Building number', 'buildingNo', { required: true, disabled, maxLength: 4, keyfilter: 'pint', hint: '4 digits' })}
                            {this.field('Additional number', 'additionalNo', { required: true, disabled, maxLength: 4, keyfilter: 'pint', hint: '4 digits' })}
                            {this.field('District', 'district', { required: true, disabled })}
                            {this.field('City', 'city', { required: true, disabled })}
                            {this.field('Postal code', 'postalCode', { required: true, disabled, maxLength: 5, keyfilter: 'pint', hint: '5 digits' })}
                            {this.field('Country', 'countryCode', { disabled: true, hint: 'ZATCA sellers are registered in Saudi Arabia' })}
                        </div>

                        {!disabled &&
                            <div className="p-col-12 p-lg-12">
                                <FooterButton
                                    access={{ addAccess: false, editAccess }}
                                    disabled={!this.state.touch}
                                    type="edit"
                                    save={this.save}
                                    reset={this.load}
                                    to=""
                                />
                            </div>
                        }
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => {
    return {
        loggedUserDetail: state.auth.userObject,
        loading: state.master.loading,
        error: state.master.error,
        success: state.master.success
    }
}

const mapDispatchToProps = dispatch => {
    return {
        processDynamicData: (model, type, data, func) => dispatch(actions.processDynamicData(model, type, data, func))
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ZatcaCompany);
